import { Component, OnInit, OnDestroy, CUSTOM_ELEMENTS_SCHEMA, ViewChild } from '@angular/core';
import { TableColumn } from '@maersk-global/mds-components-core/mc-table/types';

import { contractRatesGridColumns } from './material-rate-grid.config';
import { MasterDataManagementService } from '../../services/master-data-management.service';
import { PaginationComponent } from '../../shared-components/aems-pagination/aems-pagination.component';
import { MaterialRate } from '../../models/material-rate.model';
import { ReplaySubject, Subscription } from 'rxjs';
import { MaterialRatesFiltersComponent } from '../../components/master-data-management/shop-contract/material-rate-filters/material-rate-filters.component';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialRateFilterForm } from '../../form-model/material-rate-filter.form.model';
import { SharedDataService } from '../../services/shared-data.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ToastrService } from '../../services/toastr-service';
import { MasterDataAuditLogComponent } from '../../shared-components/master-data-audit-log/master-data-audit-log.component';
import { AuthPolicies } from '../../auth/auth-policies';
import { AuthHelper } from '../../auth/auth-helper';
@Component({
  selector: 'app-material-rate',
  standalone: true,
  templateUrl: './material-rate.component.html',
  styleUrls: ['./material-rate.component.scss'],
  imports: [
    PaginationComponent,
    MaterialRatesFiltersComponent,
    ReactiveFormsModule,
    FormsModule,
    NgxSkeletonLoaderModule,
    MasterDataAuditLogComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MaterialRateComponent implements OnInit, OnDestroy {
  filterFormGroup!: FormGroup<MaterialRateFilterForm>;
  /** Table Data */
  data: MaterialRate[] = [];
  columns: TableColumn[] = contractRatesGridColumns;
  mockDataForLoadingAnimation: { id: number }[] = Array.from({ length: 30 }, (_, i) => ({ id: i }));
  totalRecords = 0;
  currentPage = 1;
  pageSize = 20;
  firstPage?: number;
  previousPage?: number;
  nextPage?: number;
  lastPage?: number;
  /** Sorting State */
  sort: string | undefined;
  lastSortedColumn?: string;
  lastDirection: 'asc' | 'desc' = 'asc';

  /** Filter State */
  selectedFilters: any[] = [];

  /** Modal Drawer State */
  showFiltersPanel = false;

  /** Loading flag */
  loading = false;
  getMaterailRateApiCallStatus = 'NOT_STARTED';

  /** Audit log state */
  openAuditLog: boolean = false;
  selectedMreRateNumber: string | null = null;

  @ViewChild(MaterialRatesFiltersComponent) filtersComponent!: MaterialRatesFiltersComponent;
  /** API subscription reference */
  private dataSub?: Subscription;
  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  filterParameters: any | null = null;

  /** Shop codes assigned to the current user (populated for shop-role users) */
  private readonly shopUserCodes: string[] = AuthPolicies.isGSCUser()
    ? []
    : (AuthHelper.user?.maintenanceShops?.map((s) => s.maintenanceShopCode) ?? []);

  constructor(
    fb: FormBuilder,
    private readonly masterDataService: MasterDataManagementService,
    private readonly sharedDataService: SharedDataService,
    private readonly toastrService: ToastrService,
  ) {}

  ngOnInit(): void {
    this.initializeFilterForm();
    const savedFilters = this.sharedDataService.getMaterialRateFilterFormValue();
    if (savedFilters) {
      (this.filterFormGroup as FormGroup).patchValue(savedFilters);
    }
    this.getMaterialRates();
  }
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getValue(row: MaterialRate, columnId: string): string | number | Date {
    const rawValue = row[columnId as keyof MaterialRate] ?? '-';

    // Find the column config to check for a formatter
    const column = this.columns.find((col) => col.id === columnId);
    if (!column?.dataType?.formatter) {
      return rawValue;
    }

    // Apply the formatter if it exists
    try {
      return column.dataType.formatter(rawValue, row as any);
    } catch (error) {
      console.error(`Error formatting column ${columnId}:`, error);
      return rawValue;
    }
  }
  private initializeFilterForm(): void {
    this.filterFormGroup = new FormGroup<MaterialRateFilterForm>({
      shopCode: new FormControl<string[] | null>(null),
      repairMode: new FormControl<string[] | null>(null),
      repairCode: new FormControl<string[] | null>(null),
      expiryDateType: new FormControl<string | null>(null),
      expiryDate: new FormControl<string | null>(null),
      expiryDateFrom: new FormControl<string | null>(null),
      expiryDateTo: new FormControl<string | null>(null),
      effectiveDateType: new FormControl<string | null>(null),
      effectiveDate: new FormControl<string | null>(null),
      effectiveDateFrom: new FormControl<string | null>(null),
      effectiveDateTo: new FormControl<string | null>(null),
      regionCode: new FormControl<string[] | null>(null),
      areaCode: new FormControl<string[] | null>(null),
      countryCode: new FormControl<string[] | null>(null),
      applyMaterialRatesFilter: new FormControl<boolean>(false),
    });
  }
  // Subscribe to filter form changes
  /*private subscribeToFormChanges(): void {
    this.filterFormGroup.valueChanges
    .pipe(debounceTime(500), distinctUntilChanged())
    .subscribe(() => {
      //const materialRateFilterForm = this.filterFormGroup.get('materialRateFilterForm') as FormGroup;
      if (this.filterFormGroup.controls.applyMaterialRatesFilter.value) {
        this.onApplyFilter();
        this.filterFormGroup.patchValue({ applyMaterialRatesFilter: false }, { emitEvent: false });
  }
});
}*/
  // Apply filters
  onApplyFilter(): void {
    if (this.filterFormGroup.valid) {
      this.loading = true;
      const savedFilters = this.sharedDataService.getMaterialRateFilterFormValue() ?? this.filterFormGroup.value;
      if (!savedFilters) {
        return;
      }

      this.filterParameters = {
        shopCode: savedFilters.shopCode ?? [],
        repairMode: savedFilters.repairMode ?? [],
        repairCode: savedFilters.repairCode ?? [],
        expiryDateType: savedFilters.expiryDateType ?? null,
        expiryDate: savedFilters.expiryDate ?? null,
        expiryDateFrom: savedFilters.expiryDateFrom ?? null,
        expiryDateTo: savedFilters.expiryDateTo ?? null,
        effectiveDateType: savedFilters.effectiveDateType ?? null,
        effectiveDate: savedFilters.effectiveDate ?? null,
        effectiveDateFrom: savedFilters.effectiveDateFrom ?? null,
        effectiveDateTo: savedFilters.effectiveDateTo ?? null,
        regionCode: savedFilters.regionCode ?? [],
        areaCode: savedFilters.areaCode ?? [],
        countryCode: savedFilters.countryCode ?? [],
        applyMaterialRatesFilter: new FormControl<boolean | null>(false),
      };
      // Remove unused fields based on type
      if (this.filterParameters.expiryDateType === 'BETWEEN') {
        this.filterParameters.expiryDate = undefined;
      } else {
        this.filterParameters.expiryDateFrom = undefined;
        this.filterParameters.expiryDateTo = undefined;
      }
      this.currentPage = 1;
      this.getMaterialRates();
    }
    this.showFiltersPanel = false;
  }
  /** Fetch paginated data from API */
  getMaterialRates(): void {
    this.data = [];
    this.loading = true;
    this.getMaterailRateApiCallStatus = 'IN_PROGRESS';
    const apiFilters = this.buildMaterialRateApiFilters();
    this.dataSub = this.masterDataService.getMaterialRate(this.currentPage, this.pageSize, this.sort, apiFilters).subscribe({
      next: (response) => this.handleMaterialRatesResponse(response),
      error: (err) => {
        console.error('Failed to fetch material rates:', err);
        this.loading = false;
        this.getMaterailRateApiCallStatus = 'COMPLETED';
        if (err?.status !== 404) {
          this.toastrService.showToastr('Failed to load material rates. Please try again.', 'error');
        }
      },
    });
  }

  private buildMaterialRateApiFilters(): any {
    const apiFilters: any = this.filterParameters ? { ...this.filterParameters } : {};
    apiFilters.shopCode = Array.isArray(apiFilters.shopCode) ? apiFilters.shopCode : [];
    apiFilters.repairMode = Array.isArray(apiFilters.repairMode) ? apiFilters.repairMode : [];
    apiFilters.repairCode = Array.isArray(apiFilters.repairCode) ? apiFilters.repairCode : [];
    apiFilters.regionCode = Array.isArray(apiFilters.regionCode) ? apiFilters.regionCode : [];
    apiFilters.areaCode = Array.isArray(apiFilters.areaCode) ? apiFilters.areaCode : [];
    apiFilters.countryCode = Array.isArray(apiFilters.countryCode) ? apiFilters.countryCode : [];
    if (this.shopUserCodes.length > 0 && apiFilters.shopCode.length === 0) {
      apiFilters.shopCode = this.shopUserCodes;
    }
    this.cleanupMaterialDateFilter(apiFilters, 'expiryDate', 'expiryDateType', 'expiryDateFrom', 'expiryDateTo');
    this.cleanupMaterialDateFilter(apiFilters, 'effectiveDate', 'effectiveDateType', 'effectiveDateFrom', 'effectiveDateTo');
    return apiFilters;
  }

  private cleanupMaterialDateFilter(filters: any, dateKey: string, typeKey: string, fromKey: string, toKey: string): void {
    if (filters[typeKey] === 'BETWEEN') {
      delete filters[dateKey];
      if (!filters[fromKey]) delete filters[fromKey];
      if (!filters[toKey]) delete filters[toKey];
    } else {
      delete filters[fromKey];
      delete filters[toKey];
      if (!filters[dateKey]) delete filters[dateKey];
    }
  }

  private handleMaterialRatesResponse(response: any): void {
    const rawData = response.body || [];
    this.data = rawData.map((item: MaterialRate, index: number) => ({
      ...item,
      id: `${item.shopCode}__${item.repairCode}__${item.repairMode}__${index}`,
    }));
    for (const item of this.data) {
      if (item.effectiveDate) item.effectiveDate = item.effectiveDate.toString().split('T')[0];
      if (item.expiryDate) item.expiryDate = item.expiryDate.toString().split('T')[0];
    }
    const headers = response.headers;
    this.totalRecords = Number.parseInt(headers.get('total-item-count') || '0', 10);
    this.currentPage = Number.parseInt(headers.get('current-page') || '1', 10);
    this.firstPage = Number.parseInt(headers.get('first-page') || '1', 10);
    this.previousPage = Number.parseInt(headers.get('previous-page') || '0', 10);
    this.nextPage = Number.parseInt(headers.get('next-page') || '0', 10);
    this.lastPage = Number.parseInt(headers.get('last-page') || '1', 10);
    this.loading = false;
    this.getMaterailRateApiCallStatus = 'COMPLETED';
  }

  /** Pagination events */
  onPageChange(page: number): void {
    if (page !== this.currentPage) {
      this.currentPage = page;
      this.getMaterialRates();
    }
  }

  onPageSizeChange(size: number): void {
    if (size !== this.pageSize) {
      this.pageSize = size;
      this.currentPage = 1;
      this.getMaterialRates();
    }
  }

  /** Sorting logic */
  onSortDirectionChange(event: Event): void {
    let customEvent = event as CustomEvent;
    let column = customEvent.detail?.column;
    if (!column) return;

    if (this.lastSortedColumn === column) {
      this.lastDirection = this.lastDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.lastDirection = 'asc';
    }

    this.lastSortedColumn = column;
    this.sort = `${column}:${this.lastDirection}`;
    this.currentPage = 1;
    console.log('Final sort string sent to backend:', this.sort);
    this.getMaterialRates();
  }

  openFiltersModal(): void {
    const savedFilters = this.sharedDataService.getMaterialRateFilterFormValue();
    if (savedFilters) {
      (this.filterFormGroup as FormGroup).patchValue(savedFilters);
    }
    this.showFiltersPanel = true;
  }

  closeFiltersModal(): void {
    this.showFiltersPanel = false;
  }

  /** Handle filters applied from child
  onFiltersApplied(filters: any[]): void {
    this.selectedFilters = filters;
    this.currentPage = 1;
    this.showFiltersPanel = false;
    console.log('Applied filters:', filters);
    this.getMaterialRates();
  }*/

  /** Clear all filters */
  /*onFiltersCleared(): void {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.applyMaterialRatesFilter.setValue(true);
    this.getMaterialRates();
  }*/

  /** Remove a single chip
  removeFilterChip(filterToRemove: any): void {
    this.selectedFilters = this.selectedFilters.filter(f => f.key !== filterToRemove.key);
    this.filterFormGroup
      .get('materialRateFilterForm')
      ?.get(filterToRemove.key)
      ?.reset();
    this.getMaterialRates();
  }*/

  /** Handle filters applied from child component */
  onFiltersChanged(appliedFilters: any): void {
    if (!appliedFilters) return;
    // Update filter form group (keeps UI in sync without emitting valueChanges)
    this.filterFormGroup.patchValue(appliedFilters, { emitEvent: false });
    // Update local filter parameters
    this.filterParameters = {
      shopCode: appliedFilters.shopCode ?? [],
      repairMode: appliedFilters.repairMode ?? [],
      repairCode: appliedFilters.repairCode ?? [],
      expiryDateType: appliedFilters.expiryDateType ?? null,
      expiryDate: appliedFilters.expiryDate ?? null,
      expiryDateFrom: appliedFilters.expiryDateFrom ?? null,
      expiryDateTo: appliedFilters.expiryDateTo ?? null,
      effectiveDateType: appliedFilters.effectiveDateType ?? null,
      effectiveDate: appliedFilters.effectiveDate ?? null,
      effectiveDateFrom: appliedFilters.effectiveDateFrom ?? null,
      effectiveDateTo: appliedFilters.effectiveDateTo ?? null,
      regionCode: appliedFilters.regionCode ?? [],
      areaCode: appliedFilters.areaCode ?? [],
      countryCode: appliedFilters.countryCode ?? [],
    };

    // Reset to first page and fetch data
    this.currentPage = 1;
    this.getMaterialRates();

    // Close filters panel if open
    this.showFiltersPanel = false;
  }
  clearFilter(): void {
    // Reset the filter form
    this.filterFormGroup.reset();

    // Reset any local filter parameters
    this.filterParameters = null;
    this.sharedDataService.setMaterialRateFilterFormValue(null);

    // Set flag to trigger API call with default filters
    this.filterFormGroup.controls.applyMaterialRatesFilter.setValue(true);
    this.filtersComponent.clearAllSelectedFilters();

    // Reload data
    this.getMaterialRates();
  }

  onAuditLogClicked(row: MaterialRate) {
    this.selectedMreRateNumber = row.mreRateNumber;

    if (!this.selectedMreRateNumber) {
      this.toastrService.showToastr('MRE rate number not available for audit log', 'error');
      return;
    }

    this.openAuditLog = true;
  }

  onAuditLogClosed() {
    // Implement audit log close handling logic here
    this.openAuditLog = false;
  }

  readonly auditLogFetcher = this.masterDataService.getMaterialRateAuditLogs.bind(this.masterDataService);
}
